/**
 * EventFormModal
 *
 * Modal para criar/editar eventos do calendário
 * Padrão: Always-light theme (modal variables)
 * Design: "Minimal Airy" (sem cards, divisórias em fio de cabelo, micro-labels,
 * chips para enums, 1 elemento-assinatura colorido = card de contato lilás).
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { CreateCalendarEventRequest, CalendarEventResponse } from '@troia-v3/types';
import { calendarService } from '../services/calendarService';
import type { CalendarIntegration } from '../hooks/useUserCalendarIntegrations';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useAuth } from '@/contexts/AuthContext';
import { useContactsDropdown, type ContactDropdownItem } from '@/modules/contacts/hooks/useContactsDropdown';
import { ContactSearchCombobox } from '@/modules/crm/leads/components/ContactSearchCombobox';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { AttendeeData } from './AttendeeInput';
import { Icon } from '@/shared/icons'
interface EventFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  event?: CalendarEventResponse; // Se fornecido, modo edição
  defaultDate?: Date; // Data padrão para novo evento
  onSuccess?: () => void;
  calendarIntegrations: CalendarIntegration[]; // ✅ NOVO: Integrações de calendário
  initialAttendees?: AttendeeData[]; // Participantes pré-preenchidos ao abrir
  initialContact?: ContactDropdownItem | null; // Contato do CRM pré-vinculado (ex: aberto pelo chat)
}

interface EventFormData {
  summary: string;
  userId: string;  // ✅ NOVO: Responsável pelo evento
  description?: string;
  startTime: string;
  endTime: string;
  allDay: boolean;
  location?: string;
  conferenceLink?: string;  // ✅ Google Meet link (read-only para eventos existentes)
  createConference?: boolean;  // ✅ NOVO: Flag para criar conferência
  conferenceProviderId?: string;  // ✅ NOVO: ID da integração do provider
  eventType?: string;
  status?: string;
  color?: string;
  attendees?: string[];
  recurrence?: {
    frequency: string;
    interval: number;
    endDate?: string;
  };
  notifications?: Array<{ minutes: number }>;
}

const EVENT_TYPES = [
  { value: 'meeting', label: 'Reunião' },
  { value: 'task', label: 'Tarefa' },
  { value: 'reminder', label: 'Lembrete' },
  { value: 'follow_up', label: 'Retorno' },
  { value: 'activity', label: 'Atividades' },
  { value: 'other', label: 'Outro' }
];

const EVENT_STATUSES = [
  { value: 'confirmed', label: 'Confirmado' },
  { value: 'tentative', label: 'Tentativo' },
  { value: 'cancelled', label: 'Cancelado' }
];

// Cor do "dot" de cada status nas chips
const STATUS_DOT: Record<string, string> = {
  confirmed: '#51b749',
  tentative: '#fbd75b',
  cancelled: '#dc2127',
};

// Google Calendar Color IDs (1-11)
// https://developers.google.com/calendar/api/v3/reference/colors
const EVENT_COLORS = [
  { id: '1', color: '#a4bdfc', label: 'Lavanda' },      // Lavender
  { id: '2', color: '#7ae7bf', label: 'Verde Água' },   // Sage
  { id: '3', color: '#dbadff', label: 'Roxo' },         // Grape
  { id: '4', color: '#ff887c', label: 'Rosa' },         // Flamingo
  { id: '5', color: '#fbd75b', label: 'Amarelo' },      // Banana
  { id: '6', color: '#ffb878', label: 'Laranja' },      // Tangerine
  { id: '7', color: '#46d6db', label: 'Azul Claro' },   // Peacock
  { id: '8', color: '#e1e1e1', label: 'Cinza' },        // Graphite
  { id: '9', color: '#5484ed', label: 'Azul' },         // Blueberry
  { id: '10', color: '#51b749', label: 'Verde' },       // Basil
  { id: '11', color: '#dc2127', label: 'Vermelho' }     // Tomato
];

const RECURRENCE_OPTIONS = [
  { value: 'DAILY', label: 'Diariamente' },
  { value: 'WEEKLY', label: 'Semanalmente' },
  { value: 'MONTHLY', label: 'Mensalmente' },
  { value: 'YEARLY', label: 'Anualmente' }
];

const NOTIFICATION_OPTIONS = [
  { value: 5, label: '5 min antes' },
  { value: 15, label: '15 min antes' },
  { value: 30, label: '30 min antes' },
  { value: 60, label: '1 hora antes' },
  { value: 1440, label: '1 dia antes' }
];

// ── Estilos escopados sob `.tef` (Minimal Airy). Prefixo `tef-` evita colisão. ──
const TEF_CSS = `
.tef{
  --ink:#1e293b; --ink-2:#334155; --muted:#6b7280; --faint:#94a3b8;
  --line:rgba(15,23,42,.07); --line-2:#e8e8ec; --line-strong:#e5e7eb;
  --purple:#8b5cf6; --purple-600:#7c3aed; --tint:#f5f3ff; --tint-2:#ede9fe;
  --tef-font: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}
.tef, .tef *{ box-sizing:border-box; }
.tef-panel{
  width:min(660px,100%); max-height:90vh; display:flex; flex-direction:column;
  background:#fff; border:1px solid rgba(15,23,42,.06); border-radius:22px;
  font-family:var(--tef-font); color:var(--ink-2);
  box-shadow:0 1px 1px rgba(15,23,42,.04), 0 14px 34px -10px rgba(15,23,42,.22), 0 44px 84px -24px rgba(15,23,42,.28);
  overflow:hidden; animation:tef-rise .28s cubic-bezier(.2,.7,.2,1) both;
}
@keyframes tef-rise{ from{opacity:0;transform:translateY(8px) scale(.99);} to{opacity:1;transform:none;} }
.tef-hd{ flex:0 0 auto; display:flex; align-items:center; gap:15px; padding:24px 36px 20px; }
.tef-glyph{ width:44px;height:44px;border-radius:13px;flex:0 0 auto; background:var(--tint);color:var(--purple-600);
  display:grid;place-items:center; box-shadow:inset 0 0 0 1px rgba(139,92,246,.10); }
.tef-htxt{ min-width:0; }
.tef-h1{ margin:0;font-size:18px;font-weight:650;letter-spacing:-.015em;color:var(--ink); }
.tef-sub{ margin-top:2px;font-size:12.5px;color:var(--muted); }
.tef-x{ margin-left:auto;flex:0 0 auto;width:36px;height:36px;border-radius:10px;
  border:1px solid transparent;background:transparent;color:var(--faint);
  display:grid;place-items:center;cursor:pointer;transition:.15s; }
.tef-x:hover{ background:#f4f4f6;color:var(--ink-2); }
.tef-body{ flex:1 1 auto; overflow-y:auto; padding:2px 36px 4px; }
.tef-sec{ padding:20px 0;border-top:1px solid var(--line); }
.tef-sec.first{ border-top:none;padding-top:6px; }
.tef-mlabel{ display:block;font-size:11px;font-weight:600;letter-spacing:.075em;
  text-transform:uppercase;color:var(--faint);margin:0 0 12px; }
.tef-mlabel.tight{ margin-bottom:9px; }
.tef-sec-head{ display:flex;align-items:center;gap:12px;margin-bottom:14px; }
.tef-sec-head .tef-mlabel{ margin:0; }
.tef-inp{ width:100%;font-family:inherit;font-size:14px;color:var(--ink-2);background:#fff;
  border:1px solid var(--line-2);border-radius:11px;padding:11px 13px;outline:none;
  transition:border-color .15s, box-shadow .15s; }
.tef-inp::placeholder{ color:var(--faint); }
.tef-inp:hover{ border-color:#d9dce2; }
.tef-inp:focus{ border-color:var(--purple);box-shadow:0 0 0 3px rgba(139,92,246,.16); }
.tef-ta{ resize:vertical;min-height:80px;line-height:1.55; }
.tef-field{ position:relative;flex:1;min-width:0; }
.tef-fi{ position:absolute;left:13px;top:50%;transform:translateY(-50%);color:var(--faint);pointer-events:none;display:grid; }
.tef-with-icon{ padding-left:38px; }
.tef-title-input{ width:100%;border:none;outline:none;background:transparent;font-family:inherit;
  font-size:22px;line-height:1.32;font-weight:640;letter-spacing:-.017em;color:var(--ink);
  padding:2px 0;margin:0 -2px;border-radius:8px;transition:background .15s, box-shadow .15s; }
.tef-title-input::placeholder{ color:#cbd5e1;font-weight:600; }
.tef-title-input:focus{ background:#faf9ff;padding:6px 10px;margin:-4px -10px;box-shadow:0 0 0 3px rgba(139,92,246,.14); }
.tef-owner-wrap{ position:relative;display:inline-block;margin-top:14px; }
.tef-owner{ display:inline-flex;align-items:center;gap:9px;padding:5px 12px 5px 5px;border-radius:999px;
  border:1px solid transparent;background:transparent;font-family:inherit;cursor:pointer;transition:.15s; }
.tef-owner:hover{ background:#f6f6f9;border-color:var(--line-2); }
.tef-owner-pre{ font-size:12.5px;color:var(--muted);margin-right:2px; }
.tef-oname{ font-size:13.5px;font-weight:600;color:var(--ink); }
.tef-chev{ color:var(--faint);display:grid; }
.tef-owner-menu{ position:absolute;top:calc(100% + 6px);left:0;z-index:20;min-width:230px;max-height:260px;overflow-y:auto;
  background:#fff;border:1px solid var(--line-strong);border-radius:13px;padding:6px;
  box-shadow:0 10px 15px -3px rgba(15,23,42,.12), 0 4px 6px -4px rgba(15,23,42,.10); }
.tef-owner-item{ display:flex;align-items:center;gap:10px;width:100%;padding:8px 9px;border:none;background:transparent;
  border-radius:9px;cursor:pointer;font-family:inherit;text-align:left;transition:.12s; }
.tef-owner-item:hover{ background:#f5f3ff; }
.tef-oitem-name{ font-size:13.5px;color:var(--ink-2);flex:1;min-width:0; }
.tef-oitem-check{ color:var(--purple-600);display:grid; }
.tef-menu-backdrop{ position:fixed;inset:0;z-index:15; }
.tef-av{ border-radius:50%;display:grid;place-items:center;color:#fff;font-size:12px;font-weight:600;
  letter-spacing:.01em;flex:0 0 auto;width:34px;height:34px;overflow:hidden; }
.tef-av.tef-av-sm{ width:26px;height:26px;font-size:10.5px; }
.tef-av.tef-av-md{ width:30px;height:30px;font-size:11px; }
.tef-av-owner{ background:linear-gradient(135deg,#8b5cf6,#6366f1); }
.tef-av-contact{ background:linear-gradient(135deg,#f472b6,#a855f7); }
.tef-av-guest{ background:linear-gradient(135deg,#38bdf8,#6366f1); }
.tef-dt-grid{ display:flex;flex-direction:column;gap:11px; }
.tef-dt-line{ display:flex;align-items:center;gap:10px; }
.tef-dt-tag{ font-size:12.5px;color:var(--muted);width:64px;flex:0 0 auto; }
.tef-switch{ width:40px;height:23px;border-radius:999px;background:#e2e5ea;border:none;position:relative;
  cursor:pointer;transition:background .18s;flex:0 0 auto;padding:0;display:inline-block; }
.tef-switch::after{ content:"";position:absolute;top:2px;left:2px;width:19px;height:19px;border-radius:50%;
  background:#fff;box-shadow:0 1px 2px rgba(15,23,42,.25);transition:left .18s; }
.tef-switch[data-on="true"]{ background:var(--purple); }
.tef-switch[data-on="true"]::after{ left:19px; }
.tef-visually-hidden{ position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0; }
.tef-trow{ display:flex;align-items:center;gap:12px; }
.tef-tinfo{ min-width:0; }
.tef-t1{ font-size:14px;font-weight:550;color:var(--ink-2); }
.tef-t2{ font-size:12.5px;color:var(--muted);margin-top:1px; }
.tef-trow .tef-switch, .tef-trow .tef-switch-wrap{ margin-left:auto; }
.tef-ci{ width:34px;height:34px;border-radius:10px;flex:0 0 auto;background:#f5f5f7;color:#64748b;display:grid;place-items:center; }
.tef-contact{ display:flex;align-items:center;gap:14px;padding:13px 15px;border-radius:15px;
  border:1px solid #eae6fb;background:linear-gradient(180deg,#fbfaff,#f5f2ff); }
.tef-cbody{ min-width:0;flex:1; }
.tef-cname-row{ display:flex;align-items:center;gap:9px; }
.tef-cname{ font-size:14.5px;font-weight:650;color:var(--ink);letter-spacing:-.01em; }
.tef-crm{ font-size:9.5px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:var(--purple-600);
  background:#fff;border:1px solid #ddd6fe;padding:2px 7px;border-radius:999px; }
.tef-cphone{ display:flex;align-items:center;gap:6px;margin-top:3px;font-size:12.5px;color:var(--muted); }
.tef-rm{ margin-left:auto;flex:0 0 auto;width:30px;height:30px;border-radius:9px;border:1px solid transparent;
  background:transparent;color:var(--faint);display:grid;place-items:center;cursor:pointer;transition:.15s; }
.tef-rm:hover{ background:#fff;color:var(--ink-2); }
.tef-guests{ display:flex;flex-direction:column; }
.tef-guest{ display:flex;align-items:center;gap:12px;padding:10px 2px;border-top:1px solid var(--line); }
.tef-guest:first-child{ border-top:none; }
.tef-gemail{ font-size:13.5px;color:var(--ink-2); }
.tef-gstat{ font-size:11.5px;color:var(--faint);margin-top:1px; }
.tef-guest .tef-rm{ width:28px;height:28px;border-radius:8px; }
.tef-guest .tef-rm:hover{ background:#f4f4f6; }
.tef-empty{ font-size:13px;color:var(--faint);padding:2px 0 4px; }
.tef-add-guest{ display:flex;gap:9px;margin-top:12px; }
.tef-icon-btn{ width:44px;height:44px;flex:0 0 auto;border-radius:11px;border:1px solid var(--line-2);
  background:#fff;color:#64748b;display:grid;place-items:center;cursor:pointer;transition:.15s; }
.tef-icon-btn:hover{ background:#faf9ff;border-color:#ddd6fe;color:var(--purple-600); }
.tef-inline-err{ font-size:12px;color:#dc2626;margin-top:7px; }
.tef-meet{ display:flex;align-items:center;gap:12px;margin-top:14px;padding:12px 14px;border-radius:12px;
  background:var(--tint);border:1px solid #ece8fb; }
.tef-mic{ width:34px;height:34px;border-radius:9px;background:#fff;color:var(--purple-600);display:grid;place-items:center;
  flex:0 0 auto;box-shadow:inset 0 0 0 1px rgba(139,92,246,.10); }
.tef-mt{ font-size:12px;font-weight:600;color:var(--purple-600);letter-spacing:.02em; }
.tef-ml{ display:block;margin-top:1px;font-size:13.5px;color:var(--ink-2);text-decoration:none;word-break:break-all; }
.tef-ml:hover{ text-decoration:underline; }
.tef-open{ margin-left:auto;font-family:inherit;font-size:12.5px;font-weight:600;color:var(--purple-600);
  background:#fff;border:1px solid #ddd6fe;border-radius:9px;padding:7px 12px;cursor:pointer;transition:.15s;text-decoration:none; }
.tef-open:hover{ background:var(--tint-2); }
.tef-chips{ display:flex;flex-wrap:wrap;gap:8px; }
.tef-chip{ font-family:inherit;font-size:13px;font-weight:500;padding:7px 14px;border-radius:999px;
  border:1px solid var(--line-strong);background:#fff;color:#64748b;cursor:pointer;transition:.15s;
  display:inline-flex;align-items:center;gap:7px; }
.tef-chip:hover{ border-color:#d9dce2;color:var(--ink-2); }
.tef-chip[aria-pressed="true"]{ background:var(--tint);border-color:#ddd6fe;color:var(--purple-600);font-weight:600; }
.tef-dot{ width:8px;height:8px;border-radius:50%;flex:0 0 auto; }
.tef-subgroup{ margin-top:18px; }
.tef-subgroup:first-child{ margin-top:0; }
.tef-swatches{ display:flex;flex-wrap:wrap;gap:11px; }
.tef-swatch{ width:26px;height:26px;border-radius:50%;border:none;cursor:pointer;padding:0;position:relative;
  transition:transform .12s;box-shadow:inset 0 0 0 1px rgba(15,23,42,.10); }
.tef-swatch:hover{ transform:scale(1.12); }
.tef-swatch[aria-pressed="true"]{ box-shadow:0 0 0 2px #fff, 0 0 0 4px var(--purple); }
.tef-swatch[aria-pressed="true"]::after{ content:"";position:absolute;inset:0;
  background:center/14px no-repeat url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'><path d='M20 6 9 17l-5-5'/></svg>"); }
.tef-collapse{ display:flex;flex-direction:column; }
.tef-collapse + .tef-collapse{ margin-top:8px;border-top:1px solid var(--line);padding-top:8px; }
.tef-collapse-hd{ display:flex;align-items:center;gap:13px;width:100%;background:transparent;border:none;cursor:pointer;
  padding:6px 0;font-family:inherit;text-align:left; }
.tef-lead{ font-size:14px;font-weight:550;color:var(--ink-2); }
.tef-val{ margin-left:auto;font-size:13px;color:var(--muted); }
.tef-collapse-hd .tef-chev{ transition:transform .2s; }
.tef-collapse[data-open="true"] .tef-collapse-hd .tef-chev{ transform:rotate(180deg); }
.tef-collapse-body{ padding:8px 0 6px 47px; }
.tef-grid-2{ display:grid;grid-template-columns:1fr 1fr;gap:12px; }
.tef-flabel{ display:block;font-size:12px;font-weight:500;color:var(--muted);margin-bottom:5px; }
.tef-ft{ flex:0 0 auto;display:flex;align-items:center;gap:12px;padding:16px 36px 22px;border-top:1px solid var(--line);
  background:linear-gradient(180deg,#fff,#fcfcfd); }
.tef-btn{ font-family:inherit;font-size:14px;font-weight:600;border-radius:12px;padding:11px 18px;cursor:pointer;
  border:1px solid transparent;transition:.15s;display:inline-flex;align-items:center;gap:8px; }
.tef-btn:disabled{ opacity:.6;cursor:default; }
.tef-btn-danger{ background:transparent;color:#a1a1aa;padding-left:11px;padding-right:11px; }
.tef-btn-danger:hover{ color:#dc2626;background:#fef2f2; }
.tef-btn-ghost{ background:#fff;border-color:var(--line-strong);color:var(--ink-2); }
.tef-btn-ghost:hover{ background:#f8fafc;border-color:#d9dce2; }
.tef-btn-primary{ background:var(--purple);color:#fff;box-shadow:0 1px 2px rgba(124,58,237,.35), 0 4px 14px rgba(139,92,246,.28); }
.tef-btn-primary:hover{ background:var(--purple-600); }
.tef-spacer{ margin-left:auto; }
.tef-error{ margin:6px 0 0;padding:11px 13px;border-radius:11px;background:#fef2f2;border:1px solid #fecaca;
  font-size:13px;color:#b91c1c; }
.tef-spin{ width:16px;height:16px;border:2px solid rgba(255,255,255,.5);border-top-color:#fff;border-radius:50%;animation:tef-spin .7s linear infinite; }
@keyframes tef-spin{ to{ transform:rotate(360deg); } }
.tef-panel button:focus-visible, .tef-panel a:focus-visible, .tef-title-input:focus-visible,
.tef-switch:focus-visible, .tef-swatch:focus-visible{ outline:2px solid var(--purple);outline-offset:2px; }
.tef-inp:focus-visible{ outline:none; }
@media (max-width:560px){
  .tef-hd,.tef-body,.tef-ft{ padding-left:20px;padding-right:20px; }
  .tef-dt-line{ flex-wrap:wrap; }
}
/* Mobile: bottom-sheet full-width (mockup .mdl ≤640) */
@media (max-width:640px){
  .tef{ align-items:flex-end !important; justify-content:center !important; padding:0 !important; }
  .tef-panel{ width:100%; max-width:none; max-height:92vh; border-radius:18px 18px 0 0; }
}
`;

export const EventFormModal: React.FC<EventFormModalProps> = ({
  isOpen,
  onClose,
  event,
  defaultDate,
  onSuccess,
  calendarIntegrations,
  initialAttendees,
  initialContact
}) => {
  const isEditMode = !!event;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRecurrence, setShowRecurrence] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [attendees, setAttendees] = useState<AttendeeData[]>([]);
  const [linkedContact, setLinkedContact] = useState<ContactDropdownItem | null>(null);
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestError, setGuestError] = useState<string | null>(null);
  const confirm = useConfirm();

  const queryClient = useQueryClient();

  // ✅ Invalida o cache de eventos para forçar reload em QUALQUER tela que os liste
  // (Agenda usa 'calendar-events-range', listagens usam 'calendar-events').
  // Centralizado aqui para que todos os callers do modal (chat, agenda, futuros)
  // reflitam a criação/edição/exclusão sem precisar invalidar por conta própria.
  const invalidateCalendarQueries = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['calendar-events-range'], exact: false });
    await queryClient.invalidateQueries({ queryKey: ['calendar-events'], exact: false });
  };

  // ✅ Buscar usuários e usuário logado
  const { data: usersData } = useUsersDropdown({ status: 'active' });
  const { state: authState } = useAuth();
  const currentUser = authState.user;

  // ✅ Resolver nome do contato vinculado no modo edição (evento só guarda contactId).
  // Fetch condicionado: só busca quando editando um evento que TEM contactId.
  const { data: contactsForResolve } = useContactsDropdown({
    enabled: isOpen && isEditMode && !!event?.contactId,
  });

  const { register, handleSubmit, formState: { errors }, reset, watch, setValue, getValues } = useForm<EventFormData>({
    defaultValues: {
      allDay: false,
      eventType: 'meeting',
      status: 'confirmed',
      color: '9',  // Blueberry (Azul) - Google Calendar default
      attendees: [],
      notifications: [],
      createConference: true,  // ✅ Checkbox marcado por padrão
      conferenceProviderId: ''
    }
  });

  const allDay = watch('allDay');
  const notifications = watch('notifications');
  const createConference = watch('createConference');  // ✅ NOVO: Watch checkbox
  const currentUserId = watch('userId');
  const eventType = watch('eventType');
  const status = watch('status');
  const color = watch('color');

  const ownerUser = usersData?.find(u => u.id === currentUserId);

  // Iniciais para avatares (ex: "Ana Ribeiro" → "AR")
  const initials = (name?: string): string => {
    const n = (name || '').trim();
    if (!n) return '?';
    const parts = n.split(/\s+/);
    const raw = parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : parts[0].slice(0, 2);
    return raw.toUpperCase();
  };

  // Toggle notification checkbox
  const handleNotificationToggle = (minutes: number) => {
    const current = notifications || [];
    const exists = current.some(n => n.minutes === minutes);

    if (exists) {
      setValue('notifications', current.filter(n => n.minutes !== minutes));
    } else {
      setValue('notifications', [...current, { minutes }]);
    }
  };

  // Handlers de attendees
  const handleAddAttendee = (attendee: AttendeeData) => {
    setAttendees(prev => [...prev, attendee]);
  };

  const handleRemoveAttendee = (email: string) => {
    setAttendees(prev => prev.filter(a => a.email !== email));
  };

  // Adiciona convidado por e-mail (valida + dedupe)
  const addGuest = () => {
    const email = guestEmail.trim();
    if (!email) { setGuestError('Informe um e-mail'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { setGuestError('E-mail inválido'); return; }
    if (attendees.some(a => a.email === email)) { setGuestError('Este e-mail já foi adicionado'); return; }
    handleAddAttendee({ email });
    setGuestEmail('');
    setGuestError(null);
  };

  // Handler de contato vinculado (cobre selecionar / trocar / limpar).
  // ✅ Vincula via contactId top-level E, se o contato tiver email, adiciona como
  // participante (mesmo modelo da tool do agente). Ao trocar/limpar, remove o
  // participante auto-adicionado do contato anterior.
  const handleContactChange = (contact: ContactDropdownItem | null) => {
    const previousEmail = linkedContact?.email;
    setLinkedContact(contact);

    setAttendees(prev => {
      let next = prev;
      if (previousEmail && previousEmail !== contact?.email) {
        next = next.filter(a => a.email !== previousEmail);
      }
      if (contact?.email && !next.some(a => a.email === contact.email)) {
        next = [{ email: contact.email, displayName: contact.name, contactId: contact.id }, ...next];
      }
      return next;
    });
  };

  // Reset form quando modal abrir/fechar ou evento mudar
  // ✅ AGUARDA: currentUser e usersData estarem carregados antes de setar form
  useEffect(() => {
    if (isOpen && currentUser && usersData && usersData.length > 0) {
      if (event) {
        // Validate status - only allow confirmed | tentative | cancelled
        const validStatuses = ['confirmed', 'tentative', 'cancelled'];
        const eventStatus = validStatuses.includes(event.status) ? event.status : 'confirmed';

        // Modo edição: preencher com dados do evento.
        // All-day usa input type=date (date-only, parte-data UTC). O endTime salvo é
        // EXCLUSIVO, então o input de término mostra o último dia coberto (end − 1 dia).
        const isAllDay = event.allDay || false;
        reset({
          summary: event.summary,
          userId: event.userId || currentUser?.id || '',  // ✅ userId do evento
          description: event.description || '',
          startTime: isAllDay ? utcDatePart(event.startTime) : toLocalDateTimeString(event.startTime),
          endTime: isAllDay ? allDayEndInputValue(event.startTime, event.endTime) : toLocalDateTimeString(event.endTime),
          allDay: isAllDay,
          location: event.location || '',
          conferenceLink: event.conferenceData?.link || '',  // ✅ Load Google Meet link
          eventType: event.eventType || 'meeting',  // ✅ Load eventType from event
          status: eventStatus, // Use validated status
          color: event.colorId || '9',  // Default to Blueberry (Azul) if no color
          attendees: event.attendees?.map(a => a.email) || [],
          // Hidrata os chips de lembrete a partir de reminders.overrides (#132),
          // deduplicando por minutos (o form só modela minutos, não o método).
          notifications: [...new Set((event.reminders?.overrides ?? []).map(o => o.minutes))].map(minutes => ({ minutes })),
          recurrence: parseRRULE(event.recurrence)
        });
        setShowRecurrence(!!event.recurrence);
        setShowNotifications(false);

        // Inicializar attendees no modo edição
        if (event.attendees && event.attendees.length > 0) {
          setAttendees(event.attendees.map(a => ({
            email: a.email,
            displayName: a.displayName || a.email
          })));
        } else {
          setAttendees([]);
        }
      } else {
        // Modo criação: valores padrão
        const now = defaultDate || new Date();
        const start = new Date(now);
        start.setMinutes(0);
        start.setSeconds(0);

        const end = new Date(start);
        end.setHours(end.getHours() + 1);

        reset({
          summary: '',
          userId: currentUser?.id || '',  // ✅ Usuário logado por padrão
          description: '',
          startTime: toLocalDateTimeString(start),
          endTime: toLocalDateTimeString(end),
          allDay: false,
          location: '',
          eventType: 'meeting',
          status: 'confirmed',
          color: '9',  // Blueberry (Azul) - Google Calendar default
          attendees: [],
          notifications: [],
          createConference: true,  // ✅ Checkbox marcado por padrão
          conferenceProviderId: ''
        });
        setShowRecurrence(false);
        setShowNotifications(false);

        // Preencher attendees iniciais ou limpar
        setAttendees(initialAttendees && initialAttendees.length > 0 ? initialAttendees : []);
      }
      setOwnerOpen(false);
      setGuestEmail('');
      setGuestError(null);
    }
  }, [isOpen, event, defaultDate, reset, currentUser, usersData, initialAttendees]);

  // ✅ Semear/resolver o contato vinculado ao abrir (separado do reset do form
  // para não sobrescrever edições quando a lista de contatos carregar depois).
  // - Edição: evento só guarda contactId (string) → resolve o nome pela dropdown.
  // - Criação: usa initialContact (ex: pré-preenchido pelo chat).
  useEffect(() => {
    if (!isOpen) return;

    if (event) {
      if (event.contactId) {
        const resolved = contactsForResolve?.find(c => c.id === event.contactId);
        setLinkedContact(resolved ?? { id: event.contactId, name: 'Contato vinculado' });
      } else {
        setLinkedContact(null);
      }
    } else {
      setLinkedContact(initialContact ?? null);
    }
  }, [isOpen, event, initialContact, contactsForResolve]);

  // ✅ Auto-selecionar integração quando houver apenas 1
  useEffect(() => {
    if (calendarIntegrations.length === 1 && createConference) {
      setValue('conferenceProviderId', calendarIntegrations[0].id);
    }
  }, [calendarIntegrations, createConference, setValue]);

  // Ao alternar "Dia inteiro", reformatar os valores para o formato do input
  // correspondente (date = 'YYYY-MM-DD', datetime-local = 'YYYY-MM-DDTHH:mm') —
  // sem isso o input mostra vazio quando o valor não bate com o novo `type`.
  const prevAllDayRef = useRef(allDay);
  useEffect(() => {
    if (allDay === prevAllDayRef.current) return;
    prevAllDayRef.current = allDay;
    const s = getValues('startTime');
    const e = getValues('endTime');
    if (allDay) {
      if (s) setValue('startTime', s.slice(0, 10));
      if (e) setValue('endTime', e.slice(0, 10));
    } else {
      if (s) setValue('startTime', `${s.slice(0, 10)}T09:00`);
      if (e) setValue('endTime', `${e.slice(0, 10)}T10:00`);
    }
  }, [allDay, getValues, setValue]);

  // Helper function to convert recurrence object to RRULE format
  const buildRRULE = (recurrence?: EventFormData['recurrence']): string[] | undefined => {
    if (!recurrence || !recurrence.frequency) return undefined;

    let rrule = `FREQ=${recurrence.frequency}`;

    if (recurrence.interval) {
      rrule += `;INTERVAL=${recurrence.interval}`;
    }

    if (recurrence.endDate) {
      // Convert YYYY-MM-DD to YYYYMMDD format
      const until = recurrence.endDate.replace(/-/g, '');
      rrule += `;UNTIL=${until}`;
    }

    // Add RRULE: prefix as required by backend validation (RFC 5545)
    return [`RRULE:${rrule}`];
  };

  // Helper function to parse RRULE string to object (reverse of buildRRULE)
  const parseRRULE = (rruleArray?: string[]): EventFormData['recurrence'] | undefined => {
    if (!rruleArray || rruleArray.length === 0) return undefined;

    const rruleString = rruleArray[0].replace('RRULE:', '');
    const parts = rruleString.split(';');
    const result: any = {};

    parts.forEach(part => {
      const [key, value] = part.split('=');

      if (key === 'FREQ') {
        result.frequency = value as 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
      } else if (key === 'INTERVAL') {
        result.interval = value;
      } else if (key === 'UNTIL') {
        // Convert YYYYMMDD to YYYY-MM-DD
        const year = value.substring(0, 4);
        const month = value.substring(4, 6);
        const day = value.substring(6, 8);
        result.endDate = `${year}-${month}-${day}`;
      }
    });

    return result;
  };

  // Helper function to convert UTC datetime to local datetime-local format
  const toLocalDateTimeString = (utcDate: string | Date): string => {
    const date = new Date(utcDate);
    // Subtract timezone offset to get local time
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 16);
  };

  // Helper function to convert local datetime-local to UTC
  const toUTCDateTimeString = (localDateTime: string): string => {
    // datetime-local input gives us local time without timezone info
    // We need to add timezone offset to convert to UTC
    const localDate = new Date(localDateTime);
    return localDate.toISOString();
  };

  // ── Helpers all-day ──────────────────────────────────────────────────────
  // All-day é date-only ('YYYY-MM-DD', input type=date). Canônico = meia-noite UTC
  // com end EXCLUSIVO (dia seguinte ao último dia coberto) — casa com a normalização
  // do backend e o push ao Google (que faz startTime.split('T')[0]). UTC-midnight
  // mantém a parte-data correta em qualquer fuso do viewer. `slice(0,10)` torna os
  // helpers robustos caso o valor venha em datetime-local por troca de tipo do input.
  const allDayStartToUTC = (dateStr: string): string => `${dateStr.slice(0, 10)}T00:00:00.000Z`;

  const allDayEndToUTCExclusive = (dateStr: string): string => {
    const d = new Date(`${dateStr.slice(0, 10)}T00:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString();
  };

  // Parte-data UTC de um ISO/Date (para popular o input type=date na edição).
  const utcDatePart = (value: string | Date): string => {
    const d = new Date(value);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  };

  // Valor do input de término na edição: endTime é EXCLUSIVO → último dia coberto
  // = end − 1 dia; nunca antes do início (protege legados com end === start).
  const allDayEndInputValue = (startIso: string | Date, endIso: string | Date): string => {
    const startPart = utcDatePart(startIso);
    const d = new Date(endIso);
    d.setUTCDate(d.getUTCDate() - 1);
    const endPart = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    return endPart < startPart ? startPart : endPart;
  };

  const onSubmit = async (data: EventFormData) => {
    try {
      setIsLoading(true);
      setError(null);

      const eventData: CreateCalendarEventRequest = {
        summary: data.summary,
        userId: data.userId,  // ✅ NOVO: Incluir userId selecionado
        eventType: data.eventType as 'meeting' | 'task' | 'reminder' | 'follow_up' | 'other',  // ✅ Incluir eventType
        description: data.description,
        // All-day: meia-noite UTC no início e dia-seguinte-exclusivo no término
        // (não usar o conversor genérico, que trataria a data como local/UTC-midnight
        // e colapsaria start === end). Com hora: conversão local → UTC normal.
        startTime: data.allDay ? allDayStartToUTC(data.startTime) : toUTCDateTimeString(data.startTime),
        endTime: data.allDay ? allDayEndToUTCExclusive(data.endTime) : toUTCDateTimeString(data.endTime),
        allDay: data.allDay,
        location: data.location,
        colorId: data.color as '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | undefined,
        status: data.status as 'confirmed' | 'tentative' | 'cancelled',
        attendees: attendees.filter(a => a.email.trim()).map((attendee, index) => ({
          email: attendee.email,
          displayName: attendee.displayName || attendee.email,
          ...(attendee.contactId ? { contactId: attendee.contactId } : {}),  // ✅ vínculo com contato
          optional: index > 0,  // Primeiro obrigatório, resto opcional
        })),
        recurrence: showRecurrence ? buildRRULE(data.recurrence) : undefined,
        // ✅ NOVO: Incluir campos de video conferência (apenas na criação, nunca em all-day)
        createConference: !isEditMode && !data.allDay && data.createConference ? true : undefined,
        conferenceProviderId: !isEditMode && !data.allDay && data.createConference && data.conferenceProviderId
          ? data.conferenceProviderId
          : undefined,
        // Lembretes (#132): chips → reminders.overrides. Sem chips → useDefault
        // (aplica o "Lembrete padrão" da aba Geral, se ligado). Com chips →
        // lembretes próprios (method 'notification' = in-app/push).
        reminders: {
          useDefault: (data.notifications?.length ?? 0) === 0,
          overrides: (data.notifications ?? []).map((n) => ({
            method: 'notification' as const,
            minutes: n.minutes,
          })),
        },
      };

      // ✅ Vincular contato do CRM (top-level). Na edição, string vazia desvincula.
      if (linkedContact) {
        eventData.contactId = linkedContact.id;
      } else if (isEditMode) {
        eventData.contactId = '';
      }

      if (isEditMode && event) {
        // Extract original ID if editing a recurring occurrence
        // ID format: "originalId-timestamp" for expanded occurrences
        const eventId = event.id.includes('-')
          ? event.id.split('-')[0]  // Get original ObjectId
          : event.id;

        await calendarService.updateEvent(eventId, eventData);
      } else {
        await calendarService.createEvent(eventData);
      }

      await invalidateCalendarQueries();
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar evento');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!event) return;

    // Check if this is a recurring event occurrence (ID contains timestamp)
    const isRecurringOccurrence = event.id.includes('-');

    const confirmMessage = isRecurringOccurrence
      ? 'Este é um evento recorrente.\n\nAo excluir, TODAS as ocorrências deste evento serão removidas.\n\nDeseja continuar?'
      : 'Tem certeza que deseja excluir este evento?\n\nEsta ação não pode ser desfeita.';

    const confirmDelete = await confirm({ message: confirmMessage, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' });

    if (!confirmDelete) return;

    try {
      setIsLoading(true);
      setError(null);

      // Extract original ID if this is a recurring occurrence
      // ID format: "originalId-timestamp" for expanded occurrences
      const eventId = isRecurringOccurrence
        ? event.id.split('-')[0]  // Get original ObjectId
        : event.id;

      await calendarService.deleteEvent(eventId);

      await invalidateCalendarQueries();
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir evento');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const existingMeetLink = event?.conferenceData?.link;

  return createPortal(
    <div
      className="tef fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(2px)' }}
      /* form: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
    >
      <style>{TEF_CSS}</style>

      <div
        className="tef-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tef-title"
        onClick={(e) => e.stopPropagation()}
        data-testid="event-form-modal"
      >
        {/* HEADER */}
        <header className="tef-hd">
          <div className="tef-glyph" aria-hidden="true">
            <Icon name="calendar" className="w-[22px] h-[22px]" />
          </div>
          <div className="tef-htxt">
            <h2 className="tef-h1" id="tef-title">{isEditMode ? 'Editar evento' : 'Novo evento'}</h2>
            <div className="tef-sub">Agende um compromisso e vincule ao seu CRM.</div>
          </div>
          <button className="tef-x" type="button" onClick={onClose} aria-label="Fechar" data-testid="event-form-close">
            <Icon name="x" className="w-[19px] h-[19px]" />
          </button>
        </header>

        {/* BODY */}
        <div className="tef-body">
          <form onSubmit={handleSubmit(onSubmit)} id="event-form">
            {/* Hidden fields — dirigidos por setValue/chips/pill */}
            <input type="hidden" {...register('userId', { required: 'Responsável é obrigatório' })} />
            <input type="hidden" {...register('eventType')} />
            <input type="hidden" {...register('status')} />
            <input type="hidden" {...register('color')} />

            {error && <div className="tef-error" data-testid="event-form-error">{error}</div>}

            {/* 1 + 2 · Título e Responsável */}
            <section className="tef-sec first">
              <input
                className="tef-title-input"
                type="text"
                aria-label="Título do evento"
                placeholder="Adicione um título"
                data-testid="event-form-title"
                {...register('summary', { required: 'Título é obrigatório' })}
              />
              {errors.summary && <div className="tef-inline-err">{errors.summary.message}</div>}

              <div className="tef-owner-wrap">
                <button
                  type="button"
                  className="tef-owner"
                  onClick={() => setOwnerOpen(o => !o)}
                  aria-haspopup="listbox"
                  aria-expanded={ownerOpen}
                  data-testid="event-form-owner"
                >
                  <span className="tef-owner-pre">Responsável</span>
                  <span className="tef-av tef-av-owner tef-av-md" aria-hidden="true">{initials(ownerUser?.name)}</span>
                  <span className="tef-oname">{ownerUser?.name || 'Selecionar'}</span>
                  <span className="tef-chev" aria-hidden="true"><Icon name="chevron-down" className="w-[15px] h-[15px]" /></span>
                </button>
                {ownerOpen && (
                  <>
                    <div className="tef-menu-backdrop" onClick={() => setOwnerOpen(false)} />
                    <div className="tef-owner-menu" role="listbox" aria-label="Responsável">
                      {usersData?.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          role="option"
                          aria-selected={u.id === currentUserId}
                          className="tef-owner-item"
                          onClick={() => { setValue('userId', u.id, { shouldValidate: true }); setOwnerOpen(false); }}
                        >
                          <span className="tef-av tef-av-owner tef-av-sm" aria-hidden="true">{initials(u.name)}</span>
                          <span className="tef-oitem-name">{u.name}</span>
                          {u.id === currentUserId && <span className="tef-oitem-check"><Icon name="check" className="w-4 h-4" /></span>}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              {errors.userId && <div className="tef-inline-err">{errors.userId.message}</div>}
            </section>

            {/* 3 · Descrição */}
            <section className="tef-sec">
              <label className="tef-mlabel" htmlFor="tef-desc">Descrição</label>
              <textarea
                id="tef-desc"
                className="tef-inp tef-ta"
                placeholder="Adicione uma descrição, pauta ou observações…"
                data-testid="event-form-description"
                {...register('description')}
              />
            </section>

            {/* 4 · Data e horário */}
            <section className="tef-sec">
              <div className="tef-sec-head">
                <span className="tef-mlabel">Data e horário</span>
                <div className="tef-trow" style={{ marginLeft: 'auto', gap: 10 }}>
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>Dia inteiro</span>
                  <input id="tef-allday" type="checkbox" className="tef-visually-hidden" {...register('allDay')} />
                  <label htmlFor="tef-allday" className="tef-switch" data-on={allDay ? 'true' : 'false'} aria-label="Dia inteiro" data-testid="event-form-allday" />
                </div>
              </div>
              <div className="tef-dt-grid">
                <div className="tef-dt-line">
                  <span className="tef-dt-tag">Início</span>
                  <div className="tef-field">
                    <span className="tef-fi" aria-hidden="true"><Icon name="calendar" className="w-[17px] h-[17px]" /></span>
                    <input
                      className="tef-inp tef-with-icon"
                      type={allDay ? 'date' : 'datetime-local'}
                      aria-label="Início"
                      data-testid="event-form-start"
                      {...register('startTime', { required: 'Data de início é obrigatória' })}
                    />
                  </div>
                </div>
                <div className="tef-dt-line">
                  <span className="tef-dt-tag">Término</span>
                  <div className="tef-field">
                    <span className="tef-fi" aria-hidden="true"><Icon name="clock" className="w-[17px] h-[17px]" /></span>
                    <input
                      className="tef-inp tef-with-icon"
                      type={allDay ? 'date' : 'datetime-local'}
                      aria-label="Término"
                      data-testid="event-form-end"
                      {...register('endTime', { required: 'Data de término é obrigatória' })}
                    />
                  </div>
                </div>
              </div>
              {(errors.startTime || errors.endTime) && (
                <div className="tef-inline-err">{errors.startTime?.message || errors.endTime?.message}</div>
              )}
            </section>

            {/* 5 · Contato vinculado (assinatura) */}
            <section className="tef-sec">
              <span className="tef-mlabel">Contato vinculado</span>
              {linkedContact ? (
                <div className="tef-contact">
                  <span className="tef-av tef-av-contact" aria-hidden="true">{initials(linkedContact.name)}</span>
                  <div className="tef-cbody">
                    <div className="tef-cname-row">
                      <span className="tef-cname">{linkedContact.name}</span>
                      <span className="tef-crm">CRM</span>
                    </div>
                    {(linkedContact.phone || linkedContact.email) && (
                      <span className="tef-cphone">
                        <Icon name={linkedContact.phone ? 'phone' : 'mail'} className="w-[13px] h-[13px]" />
                        {linkedContact.phone || linkedContact.email}
                      </span>
                    )}
                  </div>
                  <button className="tef-rm" type="button" onClick={() => handleContactChange(null)} aria-label="Remover contato vinculado">
                    <Icon name="x" className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <ContactSearchCombobox value={null} onChange={handleContactChange} />
              )}
            </section>

            {/* 6 · Participantes */}
            <section className="tef-sec">
              <span className="tef-mlabel">Participantes</span>
              <div className="tef-guests">
                {attendees.length === 0 && <div className="tef-empty">Ninguém convidado ainda.</div>}
                {attendees.map((a) => (
                  <div className="tef-guest" key={a.email}>
                    <span className="tef-av tef-av-sm tef-av-guest" aria-hidden="true">{initials(a.displayName || a.email)}</span>
                    <div>
                      <div className="tef-gemail">{a.email}</div>
                      {a.displayName && a.displayName !== a.email && <div className="tef-gstat">{a.displayName}</div>}
                    </div>
                    <button className="tef-rm" type="button" onClick={() => handleRemoveAttendee(a.email)} aria-label={`Remover ${a.email}`}>
                      <Icon name="x" className="w-[15px] h-[15px]" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="tef-add-guest">
                <div className="tef-field">
                  <span className="tef-fi" aria-hidden="true"><Icon name="mail" className="w-[17px] h-[17px]" /></span>
                  <input
                    className="tef-inp tef-with-icon"
                    type="email"
                    value={guestEmail}
                    onChange={(e) => { setGuestEmail(e.target.value); setGuestError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGuest(); } }}
                    placeholder="Adicionar convidado por e-mail"
                    aria-label="Adicionar convidado por e-mail"
                    data-testid="event-form-guest-email"
                  />
                </div>
                <button className="tef-icon-btn" type="button" onClick={addGuest} aria-label="Adicionar convidado" data-testid="event-form-guest-add">
                  <Icon name="plus" className="w-[18px] h-[18px]" />
                </button>
              </div>
              {guestError && <div className="tef-inline-err">{guestError}</div>}
            </section>

            {/* 7 · Conferência (apenas na criação se houver integrações) */}
            {!isEditMode && calendarIntegrations.length > 0 && (
              <section className="tef-sec">
                <div className="tef-trow">
                  <span className="tef-ci" aria-hidden="true"><Icon name="video" className="w-[18px] h-[18px]" /></span>
                  <div className="tef-tinfo">
                    <div className="tef-t1">Adicionar conferência</div>
                    <div className="tef-t2">Uma sala do Google Meet será criada ao salvar</div>
                  </div>
                  <input id="tef-conf" type="checkbox" className="tef-visually-hidden" {...register('createConference')} />
                  <label htmlFor="tef-conf" className="tef-switch" data-on={createConference ? 'true' : 'false'} aria-label="Adicionar conferência" style={{ marginLeft: 'auto' }} />
                </div>

                {createConference && calendarIntegrations.length > 1 && (
                  <div style={{ marginTop: 14 }}>
                    <label className="tef-flabel" htmlFor="tef-conf-provider">Conta do Google Calendar</label>
                    <select
                      id="tef-conf-provider"
                      className="tef-inp"
                      {...register('conferenceProviderId', { required: createConference ? 'Selecione uma conta' : false })}
                    >
                      <option value="">Selecione a conta…</option>
                      {calendarIntegrations.map((integration) => (
                        <option key={integration.id} value={integration.id}>
                          Google Calendar — {integration.id.substring(0, 8)}
                        </option>
                      ))}
                    </select>
                    {errors.conferenceProviderId && <div className="tef-inline-err">{errors.conferenceProviderId.message}</div>}
                  </div>
                )}
              </section>
            )}

            {/* Google Meet existente (modo edição) */}
            {existingMeetLink && (
              <section className="tef-sec">
                <span className="tef-mlabel">Conferência</span>
                <div className="tef-meet">
                  <span className="tef-mic" aria-hidden="true"><Icon name="video" className="w-[18px] h-[18px]" /></span>
                  <div style={{ minWidth: 0 }}>
                    <span className="tef-mt">Google Meet</span>
                    <a className="tef-ml" href={existingMeetLink} target="_blank" rel="noopener noreferrer">{existingMeetLink}</a>
                  </div>
                  <a className="tef-open" href={existingMeetLink} target="_blank" rel="noopener noreferrer">Abrir</a>
                </div>
              </section>
            )}

            {/* 8 + 9 · Tipo e Status */}
            <section className="tef-sec">
              <div className="tef-subgroup">
                <span className="tef-mlabel tight">Tipo de evento</span>
                <div className="tef-chips" role="group" aria-label="Tipo de evento">
                  {EVENT_TYPES.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      className="tef-chip"
                      aria-pressed={eventType === t.value}
                      onClick={() => setValue('eventType', t.value)}
                      data-testid={`event-form-type-${t.value}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="tef-subgroup">
                <span className="tef-mlabel tight">Status</span>
                <div className="tef-chips" role="group" aria-label="Status">
                  {EVENT_STATUSES.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      className="tef-chip"
                      aria-pressed={status === s.value}
                      onClick={() => setValue('status', s.value)}
                      data-testid={`event-form-status-${s.value}`}
                    >
                      <span className="tef-dot" style={{ background: STATUS_DOT[s.value] }} />
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* 10 · Localização */}
            <section className="tef-sec">
              <label className="tef-mlabel" htmlFor="tef-local">Localização</label>
              <div className="tef-field">
                <span className="tef-fi" aria-hidden="true"><Icon name="map-pin" className="w-[17px] h-[17px]" /></span>
                <input id="tef-local" className="tef-inp tef-with-icon" type="text" placeholder="Ex: Sala 2 · Online" data-testid="event-form-location" {...register('location')} />
              </div>
            </section>

            {/* 11 · Cor do evento */}
            <section className="tef-sec">
              <span className="tef-mlabel">Cor do evento</span>
              <div className="tef-swatches" role="group" aria-label="Cor do evento">
                {EVENT_COLORS.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="tef-swatch"
                    aria-label={c.label}
                    aria-pressed={color === c.id}
                    style={{ background: c.color }}
                    onClick={() => setValue('color', c.id)}
                    title={c.label}
                  />
                ))}
              </div>
            </section>

            {/* 12 + 13 · Recorrência e Lembretes */}
            <section className="tef-sec">
              <div className="tef-collapse" data-open={showRecurrence ? 'true' : 'false'}>
                <button type="button" className="tef-collapse-hd" onClick={() => setShowRecurrence(v => !v)} aria-expanded={showRecurrence}>
                  <span className="tef-ci" aria-hidden="true"><Icon name="repeat" className="w-[18px] h-[18px]" /></span>
                  <span className="tef-lead">Recorrência</span>
                  <span className="tef-val">{showRecurrence ? 'Personalizada' : 'Não se repete'}</span>
                  <span className="tef-chev" aria-hidden="true"><Icon name="chevron-down" className="w-[17px] h-[17px]" /></span>
                </button>
                {showRecurrence && (
                  <div className="tef-collapse-body">
                    <div className="tef-grid-2">
                      <div>
                        <label className="tef-flabel">Frequência</label>
                        <select className="tef-inp" {...register('recurrence.frequency')}>
                          {RECURRENCE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="tef-flabel">Intervalo</label>
                        <input className="tef-inp" type="number" min="1" defaultValue={1} {...register('recurrence.interval')} />
                      </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <label className="tef-flabel">Termina em (opcional)</label>
                      <input className="tef-inp" type="date" {...register('recurrence.endDate')} />
                    </div>
                  </div>
                )}
              </div>

              <div className="tef-collapse" data-open={showNotifications ? 'true' : 'false'}>
                <button type="button" className="tef-collapse-hd" onClick={() => setShowNotifications(v => !v)} aria-expanded={showNotifications}>
                  <span className="tef-ci" aria-hidden="true"><Icon name="bell" className="w-[18px] h-[18px]" /></span>
                  <span className="tef-lead">Lembretes</span>
                  <span className="tef-val">{(notifications?.length || 0) > 0 ? `${notifications?.length} selecionado(s)` : 'Nenhum'}</span>
                  <span className="tef-chev" aria-hidden="true"><Icon name="chevron-down" className="w-[17px] h-[17px]" /></span>
                </button>
                {showNotifications && (
                  <div className="tef-collapse-body">
                    <div className="tef-chips" role="group" aria-label="Lembretes">
                      {NOTIFICATION_OPTIONS.map((o) => (
                        <button
                          key={o.value}
                          type="button"
                          className="tef-chip"
                          aria-pressed={(notifications || []).some(n => n.minutes === o.value)}
                          onClick={() => handleNotificationToggle(o.value)}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          </form>
        </div>

        {/* FOOTER */}
        <footer className="tef-ft">
          {isEditMode && (
            <button type="button" className="tef-btn tef-btn-danger" onClick={handleDelete} disabled={isLoading} data-testid="event-form-delete">
              <Icon name="trash" className="w-4 h-4" />
              Excluir
            </button>
          )}
          <div className="tef-spacer" />
          <button type="button" className="tef-btn tef-btn-ghost" onClick={onClose} disabled={isLoading} data-testid="event-form-cancel">
            Cancelar
          </button>
          <button type="submit" form="event-form" className="tef-btn tef-btn-primary" disabled={isLoading} data-testid="event-form-submit">
            {isLoading ? (
              <>
                <span className="tef-spin" />
                Salvando…
              </>
            ) : (
              <>
                <Icon name="calendar" className="w-4 h-4" />
                {isEditMode ? 'Salvar alterações' : 'Criar evento'}
              </>
            )}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

EventFormModal.displayName = 'EventFormModal';
